import React, { useState, useEffect } from 'react';
import { studentAPI } from '../services/api';
import AdminSidebar from '../components/AdminSidebar';
import AdminNavbar from '../components/AdminNavbar';
import { useSidebar } from '../components/SidebarContext';

const Dashboard = () => {
  const [totalStudents, setTotalStudents] = useState(0);
  const [studentsTakenTest, setStudentsTakenTest] = useState(0);
  const [totalAmountReceived, setTotalAmountReceived] = useState(0);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const { isSidebarOpen, setIsSidebarOpen } = useSidebar();
  const sidebarWidth = isSidebarOpen ? '250px' : '70px';

  // Check if current user is SuperAdmin
  const isSuperAdmin = () => {
    const role = localStorage.getItem('role');
    const adminToken = localStorage.getItem('adminToken');
    return role === 'superadmin' && adminToken;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Backend automatically handles SuperAdmin vs Admin filtering based on token
        // SuperAdmin (no collegeName in token) gets all data
        // Regular Admin gets filtered data for their college
        
        const studentsResponse = await studentAPI.stats.getTotal();
        setTotalStudents(studentsResponse.data.total || 0);

        const testTakersResponse = await studentAPI.stats.getTestTakers();
        setStudentsTakenTest(testTakersResponse.data.count || 0);

        const paymentsResponse = await studentAPI.stats.getTotalPayments();
        setTotalAmountReceived(paymentsResponse.data.total || 0);

        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setMessage('Error loading data. Please try again.');
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Inline styles
  const pageStyle = {
    display: 'flex',
    minHeight: '100vh',
    background: 'linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)',
    fontFamily: "'Roboto', sans-serif",
  };

  const mainContentStyle = {
    marginLeft: sidebarWidth,
    marginTop: '70px',
    flex: 1,
    padding: '16px',
    transition: 'margin-left 0.3s ease',
    fontFamily: "'Roboto', sans-serif",
  };

  const cardStyle = {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '13px',
    marginBottom: '18px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    fontFamily: "'Roboto', sans-serif",
  };

  const cardTitleStyle = {
    fontSize: '1rem',
    fontWeight: '600',
    marginBottom: '10px',
    color: '#6B21A8',
    fontFamily: "'Roboto', sans-serif",
  };

  const cardTextStyle = {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1F2937',
    fontFamily: "'Roboto', sans-serif",
  };

  const alertStyle = {
    backgroundColor: '#F3E8FF',
    color: '#6B21A8',
    padding: '10px',
    borderRadius: '8px',
    marginBottom: '12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontFamily: "'Roboto', sans-serif",
    animation: 'slideInFromRight 0.5s ease-out',
  };

  const closeButtonStyle = {
    background: 'none',
    border: 'none',
    fontSize: '0.875rem',
    cursor: 'pointer',
    color: '#6B21A8',
    fontFamily: "'Roboto', sans-serif",
  };

  return (
    <div style={pageStyle}>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
          font-family: 'Roboto', sans-serif !important;
        }
        /* Target sidebar tabs specifically */
        div[style*="position: fixed; top: 0; left: 0"] nav button,
        div[style*="position: fixed; top: 0; left: 0"] nav button span {
          font-family: 'Roboto', sans-serif !important;
          font-size: 16px !important;
        }
        @keyframes slideInFromRight {
          0% {
            transform: translateX(100%);
            opacity: 0;
          }
          100% {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>

      {/* Sidebar */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100%',
          width: sidebarWidth,
          zIndex: 10,
          transition: 'width 0.3s ease',
          fontFamily: "'Roboto', sans-serif",
        }}
      >
        <AdminSidebar />
      </div>

      {/* Main section */}
      <div style={mainContentStyle}>
        {/* Navbar */}
        <AdminNavbar />

        {/* Page Content */}
        <div>
          {message && (
            <div style={alertStyle}>
              <span>{message}</span>
              <button
                style={closeButtonStyle}
                onClick={() => setMessage('')}
                aria-label="Close"
              >
                ×
              </button>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center' }}>
              <span
                style={{
                  display: 'inline-block',
                  fontSize: '1.5rem',
                  color: '#3b82f6',
                  fontFamily: "'Roboto', sans-serif",
                }}
              >
                ↻ Loading...
              </span>
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', fontFamily: "'Roboto', sans-serif" }}>
              <div style={{ flex: '1 1 250px', ...cardStyle }}>
                <div style={cardTitleStyle}>
                  Total Students {isSuperAdmin() ? '' : ''}
                </div>
                <div style={cardTextStyle}>{totalStudents}</div>
              </div>
              <div style={{ flex: '1 1 250px', ...cardStyle }}>
                <div style={cardTitleStyle}>
                  Students Taken Test {isSuperAdmin() ? '' : ''}
                </div>
                <div style={cardTextStyle}>{studentsTakenTest}</div>
              </div>
              <div style={{ flex: '1 1 250px', ...cardStyle }}>
                <div style={cardTitleStyle}>
                  Total Amount Received {isSuperAdmin() ? '' : ''}
                </div>
                <div style={cardTextStyle}>₹{totalAmountReceived.toLocaleString()}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;